// InventoryV2 — batch 5's rebuild of Inventory (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on), to Astra's
// report (02-screen-review/inventory): the title and the period's name, the four reports as tabs, the period control,
// then ONE table — a row per book (one asset bought with one currency) with its quantity, its average cost, the
// period's margin and its cost-basis status — each row opening its supporting figures in place. Assumed against
// measured is said ONCE: the status pill on each row, its reason in that row's detail, the rule in the line under the
// table and in "Basis & supporting detail". A report: read-only, dense, no action doors.
//
// KEPT FROM screens/Inventory.tsx (types and fetch in components/v2/reports-pl/inventory-data.ts): the read through
// lib/api.ts's `request` (the bound Screens landed) and its key ['inventory', period, from, to]; books are never
// blended — no figure adds two books together; the basis pill on every book (an assumed cost never reads as measured;
// no cost at all is the loudest tone); a margin on an assumed or missing cost is neutral, never green or red; the red
// "No cost behind …" line first; the amber notes, with `inventory_without_cost` (the red line) and `assumed_cost`
// (while a live book's pill says it) left off; retired books folded behind one line; a period with no buys shows the
// lifetime average, marked; a failed read keeps the head and the period and is never "No books yet".
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { readState } from '../../lib/read-state';
import { displayAsset, fmtAmount, fmtDay, fmtRate } from '../../lib/format';
import { reportLabel, reportPeriod, type PeriodValue } from '../../components/PeriodChips';
import { Figure } from '../../components/ReportTable';
import { Pill } from '../../components/kit';
import type { PillTone } from '../../components/Pill';
import { EmptyState, Fact, PageHead, Panel, SkeletonRows, StateLine, TAP, ReadNotice } from '../../components/v2/kit';
import { periodLabel } from '../../components/v2/movements/PeriodControl';
import { ReportTabs } from '../../components/v2/reports-bs/ReportTabs';
import { OpenRow, PanelFoot, PeriodBar, DETAILS, RowHead, SUMMARY } from '../../components/v2/reports-pl/parts';
import { fetchInventory, type BasisSource, type InventoryBasis, type InventoryBook, type InventoryUnbased } from '../../components/v2/reports-pl/inventory-data';

/** The rows' grid from lg: book, on hand, avg cost, margin, status (literal, for the class scan). */
// Avg sell and Margin · lifetime are columns again at lg (the old table's; his "restore most the lost things", 1 Nov 2026).
const GRID = 'grid grid-cols-[minmax(12rem,1fr)_minmax(0,10rem)_minmax(0,7rem)_minmax(0,7rem)_minmax(0,9rem)_minmax(0,9rem)_7rem] items-center gap-3';
// The row's seats from 1280 (the audit at 1024, 1 Nov 2026: the book's name collapsed beside six figure seats); cards below.
const LINE_GRID = 'hidden xl:grid xl:grid-cols-[minmax(12rem,1fr)_minmax(0,10rem)_minmax(0,7rem)_minmax(0,7rem)_minmax(0,9rem)_minmax(0,9rem)_7rem] xl:items-center xl:gap-3';

/** A book's name, in the one wording: "USDT · AED". */
const bookTitle = (b: InventoryBook) => `${displayAsset(b.asset)} · ${displayAsset(b.quote_currency)}`;

/** The pill's words — the cost basis, the thing that must never be quiet. */
const BASIS_BADGE: Record<BasisSource, string> = {
  measured: 'Measured', operator: 'Your figure', peg: 'Assumed at peg', provisional: 'Provisional', unset: 'No cost set',
};
/** A measured cost is quiet; an assumption is amber; no cost at all is red (a sale against it books the whole price as profit). */
const BASIS_TONE: Record<BasisSource, PillTone> = {
  measured: 'neutral', operator: 'info', peg: 'warn', provisional: 'warn', unset: 'bad',
};
const BasisPill = ({ basis }: { basis: InventoryBasis }) => <Pill tone={BASIS_TONE[basis.source]}>{BASIS_BADGE[basis.source]}</Pill>;

/** A margin is green or red only when its cost is known; on an assumed or unset cost it is an estimate — neutral. */
const marginTone = (b: InventoryBook): 'none' | 'signed' => (b.basis.assumed || b.basis.source === 'unset' ? 'none' : 'signed');

const tradesNote = (b: InventoryBook) => (b.trades_in_period === 0
  ? 'No trades this period'
  : `${b.trades_in_period.toLocaleString('en-US')} ${b.trades_in_period === 1 ? 'trade' : 'trades'}`);

/** The average cost: the period's; a period with no buys shows the lifetime figure, marked — never a bare dash beside a held position. */
/** A rate with the currency it is quoted in, muted after it (the audit, 1 Nov 2026: avg cost and avg sell read as bare numbers). */
const Rate = ({ v, q }: { v: number; q: string }) => <span className="whitespace-nowrap"><span className="amt">{fmtRate(v, q)}</span> <span className="text-meta text-tg-hint">{displayAsset(q)}</span></span>;

function AvgCost({ book }: { book: InventoryBook }) {
  if (book.avg_buy_cost !== null) return <Rate v={book.avg_buy_cost} q={book.quote_currency} />;
  if (book.avg_buy_cost_lifetime === null) return <span className="text-tg-hint">—</span>;
  return <><Rate v={book.avg_buy_cost_lifetime} q={book.quote_currency} /><span className="block text-meta text-tg-hint">lifetime</span></>;
}

const Held = ({ book }: { book: InventoryBook }) => (
  <span className="whitespace-nowrap"><Figure value={book.quantity} asset={book.asset} /><span className="ml-1 text-meta text-tg-hint">{displayAsset(book.asset)}</span></span>
);
/** A margin says its quote currency, muted, figure and unit together: the table holds AED and INR books side by side (Astra, 1 Nov 2026). */
const Margin = ({ book, lifetime = false }: { book: InventoryBook; lifetime?: boolean }) => {
  const value = lifetime ? book.realised_margin_lifetime : book.realised_margin;
  return (
    <span className="whitespace-nowrap">
      <Figure value={value} asset={book.quote_currency} tone={marginTone(book)} />
      {value !== null && value !== undefined && <span className="ml-1 text-meta text-tg-hint">{displayAsset(book.quote_currency)}</span>}
    </span>
  );
};

/** The row as it reads closed: the laptop's grid, and the phone's stack (name, trades; quantity and the pill; cost and margin). */
function BookLine({ book }: { book: InventoryBook }) {
  const name = (
    <span className="block min-w-0">
      <span className="block break-words text-body font-medium">{bookTitle(book)}</span>
      <span className="mt-0.5 block text-meta text-tg-hint">{tradesNote(book)}</span>
    </span>
  );
  return (
    <>
      <span className={LINE_GRID}>
        {name}
        <span className="block text-right text-body"><Held book={book} /></span>
        <span className="block text-right text-body"><AvgCost book={book} /></span>
        <span className="block text-right text-body">{book.avg_sell_price_period === null ? <span className="text-tg-hint">—</span> : <Rate v={book.avg_sell_price_period} q={book.quote_currency} />}</span>
        <span className="block text-right text-body"><Margin book={book} /></span>
        <span className="block text-right text-body"><Margin book={book} lifetime /></span>
        <span className="block text-right"><BasisPill basis={book.basis} /></span>
      </span>
      <span className="block xl:hidden">
        {name}
        <span className="mt-2 flex items-center justify-between gap-3">
          <span className="min-w-0 text-body"><Held book={book} /></span>
          <span className="shrink-0"><BasisPill basis={book.basis} /></span>
        </span>
        <span className="mt-1 flex items-baseline justify-between gap-3 text-meta text-tg-hint">
          <span className="min-w-0">Avg cost <AvgCost book={book} /></span>
          <span className="shrink-0">Margin <Margin book={book} /></span>
        </span>
      </span>
    </>
  );
}

/** What stands behind a row (the pill already names its basis): the opening cost and why, then the supporting averages and the lifetime figures. */
function BookDetail({ book }: { book: InventoryBook }) {
  const { basis } = book;
  const q = book.quote_currency;
  const rate = (v: number | null) => <span className="amt">{fmtRate(v, q)}</span>;
  return (
    <>
      {basis.rate !== null && (
        <Fact label={basis.source === 'unset' ? 'Cost used' : 'Opening cost'}>
          {rate(basis.rate)}{basis.set_at && <span className="text-tg-hint"> · {fmtDay(basis.set_at, true)}</span>}
        </Fact>
      )}
      {(basis.caveat || basis.note) && <Fact label="Why">{[basis.caveat, basis.note].filter(Boolean).join(' ')}</Fact>}
      {book.avg_buy_cost !== null && book.avg_buy_cost_lifetime !== null && book.avg_buy_cost_lifetime !== book.avg_buy_cost && (
        <Fact label="Avg cost · lifetime">{rate(book.avg_buy_cost_lifetime)}</Fact>
      )}
      <Fact label="Avg sell">{rate(book.avg_sell_price_period)}</Fact>
      {book.avg_sell_price !== null && book.avg_sell_price !== book.avg_sell_price_period && (
        <Fact label="Avg sell · lifetime">{rate(book.avg_sell_price)}</Fact>
      )}
      <Fact label="Margin · lifetime"><Margin book={book} lifetime /></Fact>
      {book.book_value !== null && <Fact label="Book cost"><Figure value={book.book_value} asset={q} /> <span className="text-meta text-tg-hint">{displayAsset(q)}</span></Fact>}
      {book.last_trade_at && (
        <Fact label="Last trade">{rate(book.last_trade_rate)}<span className="text-tg-hint"> · {fmtDay(book.last_trade_at, true)}</span></Fact>
      )}
    </>
  );
}

/** Held, but on no book — ONE short red line naming the asset (Astra, 1 Nov 2026: the old sentence ran over the warning's budget and
 *  pointed at a row that is not there). The quantity and the instruction are its Details. The console has no opening-cost form yet:
 *  the books take it through the opening-basis route, so the line names what is missing and links nowhere it cannot keep. */
function UnbasedLine({ rows }: { rows: InventoryUnbased[] }) {
  if (rows.length === 0) return null;
  return (
    // A warning in the kit's box, not a coloured line (the audit, 1 Nov 2026).
    <div className="mb-4 rounded-lg border border-neg/25 bg-neg-bg px-4 py-3">
    <StateLine
      tone="bad"
      more={rows.map((r) => (
        <p key={r.asset}>
          <span className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(r.quantity, r.asset)}</span> {displayAsset(r.asset)}</span> held
          with no book behind it. Set its opening cost before trading it.
        </p>
      ))}
    >
      {rows.map((r) => displayAsset(r.asset)).join(', ')} opening cost missing
    </StateLine>
    </div>
  );
}

const HEADS = [
  { label: 'Inventory book' }, { label: 'On hand', right: true }, { label: 'Avg cost', right: true }, { label: 'Avg sell', right: true },
  { label: 'Margin', right: true }, { label: 'Margin · lifetime', right: true }, { label: 'Status', right: true },
];

export function InventoryV2() {
  const [period, setPeriod] = useState<PeriodValue>({ key: 'month' });
  // Named periods are the BFF's own Dubai windows; only "Dates…" sends days.
  const { key: asked, ...days } = reportPeriod(period);   // Yesterday goes as its two days
  const q = useQuery({
    queryKey: ['inventory', asked, days.from ?? null, days.to ?? null],
    queryFn: () => fetchInventory(asked, days),
  });
  const rs = readState(q);
  const data = q.data;
  const [showRetired, setShowRetired] = useState(false);

  const head = (
    <>
      <PageHead title="Inventory" sub={reportLabel(period, data?.period.label) ?? periodLabel(period)} />
      <ReportTabs current="/inventory" />
    </>
  );
  const bar = <PeriodBar value={period} onChange={setPeriod} />;

  // A FAILED READ is its own line — never "No books yet", and no figures beside it.
  if (rs === 'failed') {
    return (
      <>
        {head}
        {bar}
        <ReadNotice what="inventory" error={q.error} onRetry={() => { void q.refetch(); }} />
      </>
    );
  }
  if (rs === 'waiting' || !data) {
    return (
      <>
        {head}
        {bar}
        <Panel flush><SkeletonRows what="inventory" rows={4} /></Panel>
      </>
    );
  }

  // Retired books are history, not inventory: folded behind one line, nothing dropped.
  const live = data.books.filter((b) => !b.retired);
  const retired = data.books.filter((b) => b.retired);
  const info = data.notes.filter((n) => n.tone !== 'warn');
  // The amber notes still printed: `inventory_without_cost` is the red line; `assumed_cost` says what a live book's
  // own pill says, so it is left off while such a pill is on screen.
  const liveAssumed = live.some((b) => b.basis.assumed);
  const shownWarnings = data.notes.filter((n) => n.tone === 'warn')
    .filter((n) => n.code !== 'inventory_without_cost' && !(n.code === 'assumed_cost' && liveAssumed));

  const rowsOf = (books: InventoryBook[]): ReactNode => books.map((b) => (
    <OpenRow key={b.key} label={bookTitle(b)} line={<BookLine book={b} />} detail={<BookDetail book={b} />} />
  ));

  return (
    <>
      {head}
      {bar}

      {/* Inventory with no cost behind it comes first: sold as things stand, its whole price would read as profit. The ONE
          line above the table (Astra, 1 Nov 2026); the amber notes and the rule of the books are in Basis below. */}
      <UnbasedLine rows={data.unbased} />

      <Panel flush>
        {live.length === 0
          ? <EmptyState className="px-4 lg:py-16">No books yet.</EmptyState>
          : (
            <>
              <div className="hidden xl:block"><RowHead grid={GRID} heads={HEADS} /></div>
              <ul>{rowsOf(live)}</ul>
            </>
          )}
        {showRetired && retired.length > 0 && (
          <>
            <div className="border-t border-stroke bg-panel-2 px-4 py-2 text-label font-medium uppercase tracking-wider text-tg-hint lg:px-5">Retired · history only</div>
            <ul>{rowsOf(retired)}</ul>
          </>
        )}
        {(live.length > 0 || retired.length > 0) && <PanelFoot>
          {live.length} {live.length === 1 ? 'book' : 'books'}
          {retired.length > 0 && (
            <>
              {' · '}
              <button type="button" className={TAP} aria-expanded={showRetired} onClick={() => setShowRetired((v) => !v)}>
                {showRetired ? 'Hide retired' : `+ ${retired.length} retired`}
              </button>
            </>
          )}
        </PanelFoot>}
      </Panel>

      <details className={`${DETAILS} mt-4`}>
        <summary className={SUMMARY}>Basis &amp; supporting detail</summary>
        <div className="mt-2 space-y-2 leading-[1.5]">
        {shownWarnings.map((n) => <p key={n.code} className="text-warn">{n.message}</p>)}
        <p>Separate books per asset and quote currency. Assumed cost remains marked.</p>
        <p>
          Every book says where its cost came from. A measured cost is quiet; an assumed one is marked, and its margin
          is an estimate; inventory with no cost at all is red, because sold as things stand its whole sale price books
          as profit. Set an opening cost before trading it — the books take one opening cost per book, record who set
          it, and refuse it on a book that already holds a position.
        </p>
        {info.map((n) => <p key={n.code}>{n.message}</p>)}
        {retired.length > 0 && <p>Retired books were emptied into the folded books on 2 Sept and are kept for their history.</p>}
        </div>
      </details>
    </>
  );
}
